import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { clientReferencesApi, uploadApi, STORAGE_BASE_URL } from '../../services/adminApi';

const ClientReferencesForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    company_name: '',
    logo: '',
    website: '',
    sort_order: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchReference();
    }
  }, [id]);

  const fetchReference = async () => {
    try {
      setLoading(true);
      const response = await clientReferencesApi.getById(id);
      if (response.data.success) {
        const reference = response.data.data;
        setFormData({
          company_name: reference.company_name || '',
          logo: reference.logo ? `${STORAGE_BASE_URL}${reference.logo}` : '',
          website: reference.website || '',
          sort_order: reference.sort_order || 0,
          is_active: reference.is_active !== undefined ? reference.is_active : true
        });
      } else {
        setError('Referans bulunamadı');
      }
    } catch (err) {
      console.error('Reference fetch error:', err);
      setError('Referans yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) || !allowedExtensions.includes(fileExtension)) {
      alert('Sadece JPG, PNG, GIF, WEBP, SVG dosyaları yüklenebilir.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Dosya boyutu çok büyük. Maksimum 5MB olmalıdır.');
      return;
    }

    setSelectedFile(file);
    setUploading(true);

    try {
      const response = await uploadApi.upload(file);
      if (response.data.success) {
        // Backend'den dönen URL'yi frontend'de kullanmak için base URL ekle
        const logoUrl = `${STORAGE_BASE_URL}${response.data.data.url}`;
        setFormData(prev => ({
          ...prev,
          logo: logoUrl
        }));
      } else {
        alert('Dosya yüklenirken hata oluştu: ' + response.data.message);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Dosya yüklenirken hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    if (formData.logo) {
      try {
        await uploadApi.delete(formData.logo);
      } catch (err) {
        console.error('Delete file error:', err);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      logo: ''
    }));
    setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        sort_order: parseInt(formData.sort_order) || 0
      };

      let response;
      if (isEdit) {
        response = await clientReferencesApi.update(id, submitData);
      } else {
        response = await clientReferencesApi.create(submitData);
      }

      if (response.data.success) {
        alert(isEdit ? 'Referans başarıyla güncellendi' : 'Referans başarıyla oluşturuldu');
        navigate('/admin/client-references');
      } else {
        setError(response.data.message || 'Bir hata oluştu');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  };

  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#111827',
    margin: 0
  };

  const backButtonStyle = {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    padding: '0.5rem 1rem',
    borderRadius: '0.375rem',
    border: '1px solid #d1d5db',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  const errorStyle = {
    marginBottom: '1rem',
    padding: '1rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '0.5rem',
    color: '#dc2626',
    fontSize: '0.875rem'
  };

  const formStyle = {
    backgroundColor: 'white',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '0.5rem',
    padding: '1.5rem',
    border: '1px solid #e5e7eb'
  };

  const fieldStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    color: '#111827',
    backgroundColor: 'white',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none'
  };

  const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.5rem'
  };

  const checkboxStyle = {
    width: '1rem',
    height: '1rem',
    color: '#3b82f6',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    marginRight: '0.5rem',
    cursor: 'pointer'
  };

  const checkboxLabelStyle = {
    fontSize: '0.875rem',
    color: '#111827',
    cursor: 'pointer'
  };

  const helpTextStyle = {
    marginTop: '0.25rem',
    fontSize: '0.75rem',
    color: '#6b7280'
  };

  const buttonContainerStyle = {
    marginTop: '1.5rem',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem'
  };

  const cancelButtonStyle = {
    padding: '0.75rem 1.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  };

  const submitButtonStyle = {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    opacity: loading ? 0.5 : 1
  };

  const loadingStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '16rem',
    padding: '2rem'
  };

  const spinnerStyle = {
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  const logoPreviewStyle = {
    width: '4rem',
    height: '4rem',
    objectFit: 'contain',
    borderRadius: '0.5rem',
    backgroundColor: '#f3f4f6',
    border: '1px solid #e5e7eb',
    marginTop: '0.5rem'
  };

  if (loading && isEdit) {
    return (
      <>
        <Helmet>
          <title>{isEdit ? 'Referans Düzenle' : 'Yeni Referans'} - Admin Panel</title>
        </Helmet>
        <div style={containerStyle}>
          <div style={loadingStyle}>
            <div style={spinnerStyle}></div>
            <p style={{ marginLeft: '1rem', color: '#6b7280' }}>Referans yükleniyor...</p>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Referans Düzenle' : 'Yeni Referans'} - Admin Panel</title>
      </Helmet>
      
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            {isEdit ? 'Referans Düzenle' : 'Yeni Referans Ekle'}
          </h1>
          <button
            onClick={() => navigate('/admin/client-references')}
            style={backButtonStyle}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f9fafb';
              e.target.style.borderColor = '#9ca3af';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.borderColor = '#d1d5db';
            }}
          >
            ← Geri Dön
          </button>
        </div>

        {error && (
          <div style={errorStyle}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>
              Şirket Adı *
            </label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              required
              style={inputStyle}
              placeholder="Şirket adı"
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              Logo
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
                onChange={handleFileChange}
                disabled={uploading}
                style={{
                  ...inputStyle,
                  padding: '0.5rem',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.5 : 1
                }}
              />
              {formData.logo && (
                <button
                  type="button"
                  onClick={removeLogo}
                  disabled={uploading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    opacity: uploading ? 0.5 : 1
                  }}
                >
                  Logoyu Kaldır
                </button>
              )}
            </div>
            <p style={helpTextStyle}>
              JPG, PNG, GIF, WEBP, SVG dosyaları (maksimum 5MB)
            </p>
            {uploading && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '0.5rem',
                color: '#3b82f6',
                fontSize: '0.875rem'
              }}>
                <div style={{
                  width: '1rem',
                  height: '1rem',
                  border: '2px solid #e5e7eb',
                  borderTop: '2px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Yükleniyor...
              </div>
            )}
            {formData.logo && !uploading && (
              <div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>Logo Önizleme:</p>
                <img 
                  src={formData.logo} 
                  alt="Logo önizleme"
                  style={logoPreviewStyle}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{ 
                  ...logoPreviewStyle, 
                  display: 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#dc2626',
                  fontSize: '0.75rem'
                }}>
                  Logo yüklenemedi
                </div>
              </div>
            )}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              Website URL
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              style={inputStyle}
              placeholder="https://example.com"
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
            <p style={helpTextStyle}>
              Şirketin website adresi (opsiyonel)
            </p>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              Sıralama
            </label>
            <input
              type="number"
              name="sort_order"
              value={formData.sort_order}
              onChange={handleChange}
              min="0"
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
            <p style={helpTextStyle}>
              Liste sıralaması (0 = en üstte)
            </p>
          </div>

          <div style={checkboxContainerStyle}>
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              style={checkboxStyle}
            />
            <label style={checkboxLabelStyle}>
              Aktif
            </label>
          </div>

          <div style={buttonContainerStyle}>
            <button
              type="button"
              onClick={() => navigate('/admin/client-references')}
              style={cancelButtonStyle}
              onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              style={submitButtonStyle}
              onMouseOver={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#2563eb';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#3b82f6';
                }
              }}
            >
              {loading ? 'Kaydediliyor...' : (isEdit ? 'Güncelle' : 'Oluştur')}
            </button>
          </div>
        </form>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default ClientReferencesForm;
